import { getAuthHeaders, makeApiCall, readJsonResponse } from '../../../utils/apiConfig';
import type { AdminCanonicalProduct, AdminProductDetail, RepairToolId, ToolResult } from './productDetailTypes';

const base = (id: string) => `/normalization/canonical/${encodeURIComponent(id)}`;

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await makeApiCall(path, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()), ...(init.headers || {}) },
  });
  const body = (await readJsonResponse(response, 'The server sent an unreadable response.')) as { success?: boolean; data?: T; error?: unknown; message?: string };
  if (!response.ok || body?.success === false) {
    const err = body?.error;
    const message = typeof err === 'string' ? err : (err as { message?: string })?.message || body?.message || 'Request failed';
    // The status rides along so a caller can tell "not found" from a failure worth retrying.
    throw Object.assign(new Error(message), { status: response.status });
  }
  return body.data as T;
}

export const productDetailApi = {
  detail: (id: string) => call<AdminProductDetail>(`${base(id)}/admin-detail`),

  /** Save edits. `editSource` 'ai' marks the fields as accepted AI suggestions rather than manual. */
  update: (id: string, payload: Record<string, unknown>, editSource: 'manual' | 'ai' = 'manual') =>
    call<AdminCanonicalProduct>(base(id), { method: 'PUT', body: JSON.stringify({ ...payload, _editSource: editSource }) }),

  remove: (id: string) => call<unknown>(base(id), { method: 'DELETE' }),

  runTool: (id: string, tool: RepairToolId) => call<ToolResult>(`${base(id)}/tools/${tool}`, { method: 'POST', body: '{}' }),

  /** Release a manual lock so the next staged promotion may update the field again. */
  releaseManual: (id: string, paths: string[]) =>
    call<AdminCanonicalProduct>(`${base(id)}/manual-fields/release`, { method: 'POST', body: JSON.stringify({ paths }) }),
};
